import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { ArrowRight, ArrowLeftRight, DollarSign, RefreshCw, Clock } from 'lucide-react';
import SEOHead from './SEOHead';
import { currencyUnits, popularCurrencyConversions, getCurrencyById, fetchExchangeRates, convertCurrency } from '../data/currencyRoutes';

// Format numbers for display
const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  if (num === 0) return '0';
  if (Math.abs(num) < 0.01) {
    return num.toFixed(6);
  }
  if (Math.abs(num) < 1) {
    return num.toFixed(4);
  }
  if (Math.abs(num) > 1000000) {
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return num.toFixed(2);
};

const CurrencyConverterIndex = () => {
  const { t } = useTranslation();
  const [fromUnit, setFromUnit] = useState('usd');
  const [toUnit, setToUnit] = useState('eur');
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');
  const [rates, setRates] = useState(null);
  const [rateDate, setRateDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Fetch exchange rates
  const loadRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchExchangeRates('EUR');
      setRates(data.rates);
      setRateDate(data.date);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      setError(t('currency.fetchError', 'Failed to fetch exchange rates. Please try again.'));
      setLoading(false);
    }
  }, [t]);

  // Load rates on mount
  useEffect(() => {
    loadRates();
  }, [loadRates]);

  // Perform conversion when values change
  useEffect(() => {
    if (rates && fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertCurrency(numValue, fromUnit, toUnit, rates);
        if (result !== null) {
          setToValue(formatNumber(result));
        } else {
          setToValue('');
        }
      } else {
        setToValue('');
      }
    }
  }, [fromValue, fromUnit, toUnit, rates]);

  const handleSwap = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue.replace(/,/g, ''));
  };

  const handleRefresh = () => {
    loadRates();
  };

  const fromUnitData = getCurrencyById(fromUnit);
  const toUnitData = getCurrencyById(toUnit);

  // Get current exchange rate for display
  const getCurrentRate = () => {
    if (!rates || !fromUnitData || !toUnitData) return null;
    const fromRate = rates[fromUnitData.code];
    const toRate = rates[toUnitData.code];
    if (!fromRate || !toRate) return null;
    return toRate / fromRate;
  };

  const currentRate = getCurrentRate();

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('converters.currency.name')}
        description={t('converters.currency.seoDescription')}
      />

      {/* Header with Description */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-white" />
            <h1 className="text-lg font-semibold text-white">{t('converters.currency.title', t('converters.currency.name'))}</h1>
          </div>
        </div>
        <div className="p-4">
          <p className="text-gray-600 text-sm">
            {t('converters.currency.description')}
          </p>
          {rateDate && (
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>{t('currency.ratesUpdated', 'Rates updated')}: {rateDate}</span>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                title={t('currency.refreshRates', 'Refresh rates')}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            {t('currency.tryAgain', 'Try again')}
          </button>
        </div>
      )}

      {/* Main Converter Tool */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            {/* From Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('common.from')}</label>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                placeholder={t('common.enterValue')}
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              >
                {currencyUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {t(`currencyUnits.${unit.id}.name`, unit.name)} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={handleSwap}
                className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
                title={t('common.swap')}
              >
                <ArrowLeftRight size={24} />
              </button>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('common.to')}</label>
              <input
                type="text"
                value={loading ? t('currency.loading', 'Loading...') : toValue}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg"
                placeholder={t('common.result')}
              />
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              >
                {currencyUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {t(`currencyUnits.${unit.id}.name`, unit.name)} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Result Display */}
          {fromValue && toValue && fromUnitData && toUnitData && !loading && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span>{' '}
                <span className="text-gray-500">{fromUnitData.symbol}</span> ={' '}
                <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span>{' '}
                <span className="text-gray-500">{toUnitData.symbol}</span>
              </p>
              {currentRate && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  1 {fromUnitData.symbol} = {formatNumber(currentRate)} {toUnitData.symbol}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Quick Reference Table */}
        {rates && (
          <div className="border-t border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {fromUnitData?.name} ({fromUnitData?.symbol})
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {toUnitData?.name} ({toUnitData?.symbol})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 5, 10, 25, 50, 100, 500, 1000].map((val) => {
                    const result = convertCurrency(val, fromUnit, toUnit, rates);
                    return (
                      <tr key={val} className="border-t border-gray-100">
                        <td className="px-3 py-2">{val} {fromUnitData?.symbol}</td>
                        <td className="px-3 py-2 text-[#4a7c9b] font-medium">
                          {result !== null ? formatNumber(result) : '-'} {toUnitData?.symbol}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Popular Currency Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('converters.currency.popularTitle')}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {popularCurrencyConversions.map((conversion, index) => {
              const fromU = getCurrencyById(conversion.from);
              const toU = getCurrencyById(conversion.to);
              if (!fromU || !toU) return null;

              return (
                <LocalizedLink
                  key={index}
                  to={`/convert/currency/${conversion.from}-to-${conversion.to}`}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-blue-50 text-gray-700 hover:text-[#2563eb] transition-colors group"
                >
                  <span className="text-sm">
                    {fromU.symbol} to {toU.symbol}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-[#2563eb] transition-colors" />
                </LocalizedLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* Complete List of Currencies */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('currency.allSupportedCurrencies', 'All Supported Currencies')} ({currencyUnits.length})</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currencyUnits.map((unit) => (
              <div key={unit.id} className="border border-gray-200 rounded-lg p-3">
                <h3 className="font-medium text-gray-800 mb-2">
                  {t(`currencyUnits.${unit.id}.name`, unit.name)} ({unit.symbol})
                </h3>
                <div className="space-y-1">
                  {currencyUnits
                    .filter((u) => u.id !== unit.id)
                    .slice(0, 5)
                    .map((targetUnit) => (
                      <LocalizedLink
                        key={targetUnit.id}
                        to={`/convert/currency/${unit.id}-to-${targetUnit.id}`}
                        className="block text-sm text-gray-600 hover:text-[#2563eb] transition-colors"
                      >
                        {unit.symbol} {t('common.to').replace(':', '')} {targetUnit.symbol}
                      </LocalizedLink>
                    ))}
                  <LocalizedLink
                    to={`/convert/currency/${unit.id}-to-${currencyUnits.find(u => u.id !== unit.id)?.id}`}
                    className="block text-sm text-[#2563eb] hover:underline mt-1"
                  >
                    {t('common.moreConversions', { unit: unit.symbol })}
                  </LocalizedLink>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Source Info */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-600">
          <strong>{t('currency.dataSource', 'Data Source')}:</strong> {t('currency.dataSourceInfo', 'Exchange rates are provided by the European Central Bank (ECB) via')}{' '}
          <a href="https://www.frankfurter.app/" target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">
            Frankfurter API
          </a>
          . {t('currency.ratesUpdateTime', 'Rates are updated on European business days around 16:00 CET.')}
        </p>
      </div>

      {/* Back to Home Link */}
      <div className="text-center">
        <LocalizedLink
          to="/"
          className="text-[#2563eb] hover:underline text-sm"
        >
          {t('common.backToAllConverters')}
        </LocalizedLink>
      </div>
    </div>
  );
};

export default CurrencyConverterIndex;
